import {
  createContext,
  useContext,
  useState,
  useMemo,
  type ReactNode,
  type Dispatch,
  type SetStateAction
} from "react";

// Context types
export interface MaterialLogContextValue {
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
  sidebarMetadata: {
    feature: "comments" | string;
    itemDetails: any | null;
  } | null;
  setSidebarMetadata: Dispatch<
    SetStateAction<{
      feature: "comments" | string;
      itemDetails: any | null;
    } | null>
  >;
}

// Create context
const MaterialLogContext = createContext<MaterialLogContextValue | null>(null);

// Custom hook for accessing the context
export function useMaterialLogContext() {
  const context = useContext(MaterialLogContext);
  if (!context) {
    throw new Error(
      "useMaterialLogContext must be used within MaterialLogProvider"
    );
  }
  return context;
}

// Provider component
export function MaterialLogProvider({ children }: { children: ReactNode }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [sidebarMetadata, setSidebarMetadata] = useState<{
    feature: string;
    itemDetails: any | null;
  } | null>({
    feature: "",
    itemDetails: null
  });

  const value = useMemo(
    () => ({
      isSidebarOpen,
      setIsSidebarOpen,
      sidebarMetadata,
      setSidebarMetadata
    }),
    [isSidebarOpen, sidebarMetadata]
  );

  return (
    <MaterialLogContext.Provider value={value}>
      {children}
    </MaterialLogContext.Provider>
  );
}
